import { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Popover, PopoverTrigger, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { NotificationState } from 'root/src/shared/notification/notification.types';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useNotifications } from 'root/src/client/v2/notifications/redesign/useNotifications';
import { NotificationItem } from 'root/src/client/v2/notifications/redesign/NotificationItem';
import { NotificationModalContent } from 'root/src/client/v2/notifications/redesign/NotificationModal';
const MAX_BADGE_COUNT = 9;
export const NotificationBell = ({ enabled, className }) => {
    const [open, setOpen] = useState(false);
    const reduceMotion = useReducedMotion();
    const redesignModal = useRedesignModal();
    const { items, unreadCount, isLoading, isError, markRead, markAllRead, markingAll } = useNotifications({ enabled, open });
    const badgeLabel = unreadCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(unreadCount);
    const ariaLabel = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';
    // Open the clicked notification full in the shared modal. Close the popover first (its content
    // is z-1102, above the modal), and mark unread ones read so the badge updates immediately.
    const handleOpen = (notification) => {
        setOpen(false);
        if (notification.state === NotificationState.Unread) {
            markRead(notification.id).catch(() => undefined);
        }
        redesignModal.show(<NotificationModalContent notifications={[notification]} closeable markReadOnAdvance={false}/>, { closeable: true });
    };
    return (<Popover open={open} onOpenChange={setOpen}>
			<TooltipProvider delayDuration={200}>
				<Tooltip>
					<TooltipTrigger asChild>
						<PopoverTrigger asChild>
							<button type="button" aria-label={ariaLabel} className={cn('relative flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground', open && 'bg-white/[0.06] text-foreground', className)}>
								<NotificationsNoneIcon sx={{ fontSize: 20 }}/>
								{unreadCount > 0 && (<span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-neon-pink px-1 text-[10px] font-bold leading-none text-white shadow-[0_0_10px_-1px_hsl(var(--neon-pink)/0.8)]">
										{badgeLabel}
									</span>)}
							</button>
						</PopoverTrigger>
					</TooltipTrigger>
					<TooltipContent>Notifications</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<PopoverContent align="end" className="w-[360px] max-w-[calc(100vw-2rem)] p-0" onCloseAutoFocus={(event) => {
            // On close Radix returns focus to the bell trigger, which re-opens its hover/focus
            // tooltip ("Notifications") - and when we close to open a notification modal it
            // lands behind the modal. Drop the focus return so the tooltip stays hidden.
            event.preventDefault();
        }}>
				<TooltipProvider delayDuration={200}>
					<div className="flex items-center justify-between gap-2 border-b border-solid border-border px-4 py-3">
						<div className="flex items-center gap-2">
							<span className="font-display text-sm font-semibold text-foreground">Notifications</span>
							{unreadCount > 0 && (<span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neon-pink/15 px-1.5 text-[11px] font-semibold text-neon-pink">
									{unreadCount}
								</span>)}
						</div>
						{unreadCount > 0 && (<button type="button" onClick={markAllRead} aria-busy={markingAll} className={cn('inline-flex items-center gap-1 text-xs font-medium text-neon-cyan transition-opacity hover:opacity-80', markingAll && 'pointer-events-none opacity-60')}>
								<DoneAllIcon sx={{ fontSize: 14 }}/>
								Mark all read
							</button>)}
					</div>

					<div className="max-h-[min(70vh,420px)] overflow-y-auto">
						{isLoading && (<div className="space-y-3 p-4">
								{[0, 1, 2].map(row => (<div key={row} className="flex gap-3">
										<Skeleton className="mt-1 h-2 w-2 rounded-full"/>
										<div className="flex-1 space-y-1.5">
											<Skeleton className="h-3.5 w-full"/>
											<Skeleton className="h-3.5 w-2/3"/>
											<Skeleton className="h-3 w-20"/>
										</div>
									</div>))}
							</div>)}

						{!isLoading && isError && (<div className="px-4 py-10 text-center text-sm text-muted-foreground">
								Could not load notifications. Please try again.
							</div>)}

						{!isLoading && !isError && items.length === 0 && (<div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
								<NotificationsNoneIcon sx={{ fontSize: 28 }} className="text-muted-foreground/50"/>
								<p className="text-sm font-medium text-foreground">You are all caught up</p>
								<p className="text-xs text-muted-foreground">New notifications will show up here.</p>
							</div>)}

						{!isLoading && !isError && items.length > 0 && (<ul className="m-0 list-none divide-y divide-border p-0">
								<AnimatePresence initial={false}>
									{items.map((notification, index) => (<motion.li key={notification.id} layout={!reduceMotion} initial={reduceMotion ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={reduceMotion ? undefined : { opacity: 0 }} transition={{ duration: 0.2, delay: Math.min(index, 6) * 0.03, ease: 'easeOut' }}>
											<NotificationItem notification={notification} onMarkRead={markRead} onOpen={handleOpen}/>
										</motion.li>))}
								</AnimatePresence>
							</ul>)}
					</div>
				</TooltipProvider>
			</PopoverContent>
		</Popover>);
};
